import { useEffect, useState } from 'react';

// The CRM already injects this script from PlacesAutocomplete, so we reuse whatever tag
// is on the page instead of loading a second copy (two loaders on one page makes Google
// log "You have included the Google Maps JavaScript API multiple times" and can leave
// google.maps half-initialised). Matched on the HOST only, exactly like
// PlacesAutocomplete does, so any existing Maps tag counts whatever its query string is.
const SRC = 'maps.googleapis.com';
let inflight = null;

const fetchKey = async () => {
  const build = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (build) return build;
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/maps-key`);
  if (!res.ok) throw new Error('Google Maps key is not configured');
  return (await res.json()).api_key;
};

const waitFor = (test, timeout = 12000, step = 120) => new Promise((resolve, reject) => {
  const started = Date.now();
  const tick = () => {
    if (test()) { resolve(); return; }
    if (Date.now() - started > timeout) {
      reject(new Error('Google Maps did not finish loading'));
      return;
    }
    setTimeout(tick, step);
  };
  tick();
});

// `loading=async` hands you a LOADER, not the classes: `window.google.maps` exists while
// `google.maps.Map` is still undefined, so a naive onload handler dies with "g.Map is not
// a constructor". importLibrary() is the documented wait, and the poll after it covers
// the case where another tag on the page loaded the API a different way.
const ensureLibraries = async () => {
  await waitFor(() => Boolean(window.google?.maps));
  const maps = window.google.maps;
  if (typeof maps.importLibrary === 'function') {
    try {
      await Promise.all(['core', 'maps', 'marker'].map((lib) => maps.importLibrary(lib)));
    } catch {
      /* the classes may already be attached — the poll below is the real gate */
    }
  }
  await waitFor(() => typeof window.google?.maps?.Map === 'function');
};

const load = async () => {
  if (typeof window.google?.maps?.Map === 'function') return;
  const existing = document.querySelector(`script[src*="${SRC}"]`);
  if (!existing) {
    const key = await fetchKey();
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Google Maps failed to load'));
      document.head.appendChild(script);
    });
  }
  await ensureLibraries();
};

/** Loads the Maps JS API once per page. `enabled=false` costs nothing — a map load is
 *  billable, so nothing is fetched until a map is actually about to be drawn. */
export const useGoogleMaps = (enabled = true) => {
  const [ready, setReady] = useState(typeof window.google?.maps?.Map === 'function');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || ready) return undefined;
    inflight = inflight || load();
    let alive = true;
    inflight
      .then(() => { if (alive) { setReady(true); setError(null); } })
      .catch((e) => { inflight = null; if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [enabled, ready]);

  return { ready, error };
};

export default useGoogleMaps;
